import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useCollectingFormContext } from '../context/collectingContext'
import { getBusinessAssociations } from '../services/businessAssociationsService'
import { createSelfBusinessAssociation } from '../utils/businessAssociationUtils'
import { useUser } from './useUser'

export const useBusinessAssociations = () => {
  const { t } = useTranslation()
  const {
    values,
    setUserName,
    selectedBusinessAssociation,
    setSelectedBusinessAssociation,
    setBusinessAssociationSelectionReady,
  } = useCollectingFormContext()
  const { user, customerNumber } = useUser()

  const name = user?.name || ''
  const userNameForQuery = user?.preferred_username || ''

  useEffect(() => {
    if (userNameForQuery && !values.userName) {
      setUserName(userNameForQuery)
    }
  }, [userNameForQuery, setUserName, values.userName])

  const selfBusinessAssociation = useMemo(
    () =>
      customerNumber
        ? createSelfBusinessAssociation({
            customerNumber,
            name,
            role: t('userMenu.role'),
          })
        : null,
    [customerNumber, name, t]
  )

  const { data: fetchedBusinessAssociations, isLoading } = useQuery({
    queryKey: ['businessAssociations', userNameForQuery],
    queryFn: () => getBusinessAssociations(userNameForQuery),
    enabled: !!userNameForQuery,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnReconnect: false,
  })

  const businessAssociations = useMemo(
    () =>
      selfBusinessAssociation
        ? [selfBusinessAssociation, ...(fetchedBusinessAssociations ?? [])]
        : (fetchedBusinessAssociations ?? []),
    [selfBusinessAssociation, fetchedBusinessAssociations]
  )

  const compactBusinessAssociations = useMemo(() => {
    const defaultAssociations = businessAssociations.slice(0, 10)

    if (
      !selectedBusinessAssociation ||
      defaultAssociations.some(
        association => association.id === selectedBusinessAssociation.id
      )
    ) {
      return defaultAssociations
    }

    if (defaultAssociations.length < 10) {
      return [...defaultAssociations, selectedBusinessAssociation]
    }

    return [...defaultAssociations.slice(0, -1), selectedBusinessAssociation]
  }, [businessAssociations, selectedBusinessAssociation])

  useEffect(() => {
    if (isLoading) {
      setBusinessAssociationSelectionReady(false)
      return
    }

    if (businessAssociations.length === 0) {
      setBusinessAssociationSelectionReady(true)
      return
    }

    if (!selectedBusinessAssociation) {
      setSelectedBusinessAssociation(businessAssociations[0])
      return
    }

    const isSelectionStillAvailable = businessAssociations.some(
      association => association.id === selectedBusinessAssociation.id
    )

    if (!isSelectionStillAvailable) {
      setSelectedBusinessAssociation(businessAssociations[0])
      return
    }

    setBusinessAssociationSelectionReady(true)
  }, [
    businessAssociations,
    isLoading,
    selectedBusinessAssociation,
    setSelectedBusinessAssociation,
    setBusinessAssociationSelectionReady,
  ])

  return {
    businessAssociations,
    compactBusinessAssociations,
    isLoading,
    selectedBusinessAssociation,
    setSelectedBusinessAssociation,
  }
}
